/**
 * Token Launch API endpoints
 * IPFS upload, trade creation, launch timing, Pinata
 */

import { request, ApiError, API_BASE } from './core';

// ============================================
// PUMP.FUN (via backend proxy)
// ============================================

export async function uploadToIPFS(tokenData) {
  const {
    name,
    symbol,
    description,
    twitter,
    telegram,
    website,
    showName,
    imageBase64,
    imageName,
  } = tokenData;

  return request('/api/ipfs', {
    method: 'POST',
    body: JSON.stringify({
      name,
      symbol,
      description,
      twitter,
      telegram,
      website,
      showName: showName || 'true',
      imageBase64,
      imageName,
    }),
  });
}

export async function createTrade(tradeData) {
  const response = await fetch(`${API_BASE}/api/trade`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(tradeData),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new ApiError(error || 'Trade failed', 'TRADE_ERROR', response.status);
  }

  return response.arrayBuffer();
}

// ============================================
// PINATA (IPFS)
// ============================================

export async function uploadToIPFSViaPinata(fileBase64, fileName, contentType = 'image/png') {
  return request('/api/pinata/file', {
    method: 'POST',
    body: JSON.stringify({ fileBase64, fileName, contentType }),
  });
}

export async function uploadJsonToIPFS(metadata, name) {
  return request('/api/pinata/json', {
    method: 'POST',
    body: JSON.stringify({ metadata, name }),
  });
}

export async function uploadTokenMetadataToIPFS({
  name,
  symbol,
  description,
  imageBase64,
  twitter,
  telegram,
  website,
}) {
  return request('/api/pinata/token-metadata', {
    method: 'POST',
    body: JSON.stringify({ name, symbol, description, imageBase64, twitter, telegram, website }),
  });
}

export async function getPinataStatus(ipfsHash) {
  return request(`/api/pinata/status/${ipfsHash}`);
}

// ============================================
// TOKEN LAUNCHER (The Trigger)
// ============================================

/**
 * Triggers the BullMQ Worker sequence
 * @param {Object} params - Launch parameters
 * @param {Object} params.tokenMetadata - { name, symbol, image, description, ... }
 * @param {String} params.mode - 'HYBRID' (user-signed) or 'AUTO' (server-signed)
 * @param {String} params.serializedTx - Base64 serialized transaction (for HYBRID mode)
 * @param {Number} params.jitoTipAmount - Jito tip in SOL
 * @param {String} params.network - Network ('mainnet' or 'devnet')
 * @param {String} params.vanityKeyId - Optional: Pre-generated vanity key ID from pool (AUTO mode)
 * @param {String} userId - The wallet address or session ID
 */
export async function launchToken(params, userId) {
  const requestBody =
    typeof params === 'object' && !params.tokenMetadata
      ? { tokenMetadata: params, userId }
      : typeof params === 'object' && params.tokenMetadata
        ? { ...params, userId }
        : { tokenMetadata: params, userId };

  return request('/api/queue/launch', {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
}

// ============================================
// LAUNCH TIMING ORACLE
// ============================================

export async function recordLaunch(launchData) {
  return request('/api/launchtiming/record', {
    method: 'POST',
    body: JSON.stringify(launchData),
  });
}

export async function getOptimalLaunchTimes() {
  return request('/api/launchtiming/optimal');
}

export async function getCurrentLaunchConditions() {
  return request('/api/launchtiming/current');
}

export async function getLaunchHistory(limit = 50) {
  return request(`/api/launchtiming/history?limit=${limit}`);
}

export async function getLaunchReadiness() {
  return request('/api/launchtiming/readiness');
}

// ============================================
// VANITY POOL (Instant Vanity Addresses)
// ============================================

/**
 * Get available vanity patterns with stock levels
 * @returns {Promise<{patterns: Object, categories: string[]}>}
 */
export async function getVanityPatterns() {
  return request('/api/vanity/patterns');
}

/**
 * Get available vanity suffixes with counts
 * @returns {Promise<{suffixes: Array, categories: string[]}>}
 */
export async function getVanitySuffixes() {
  return request('/api/vanity/suffixes');
}

/**
 * Reserve (preview) a vanity address - returns public key for live preview
 * Does NOT claim the key - used for instant preview in UI
 * @param {string} suffix - Desired suffix
 * @param {boolean} exactCase - Prefer exact case match
 * @returns {Promise<{publicKey: string, suffix: string, preview: string}>}
 */
export async function reserveVanityPreview(suffix, exactCase = false) {
  return request(`/api/vanity/reserve?suffix=${encodeURIComponent(suffix)}&exactCase=${exactCase}`);
}

/**
 * Checkout (claim) a vanity address from the pool
 * Returns a keyId to pass to launch, NOT the private key
 * @param {Object} params
 * @param {string} params.suffix - Desired suffix
 * @param {boolean} params.exactCase - Require exact case match (premium)
 * @param {string} params.userWallet - User's wallet address
 * @returns {Promise<{publicKey: string, keyId: string, suffix: string}>}
 */
export async function checkoutVanityAddress({ suffix, exactCase = false, userWallet }) {
  return request('/api/vanity/checkout', {
    method: 'POST',
    body: JSON.stringify({ suffix, exactCase, userWallet }),
  });
}

/**
 * Get vanity pool status
 * @returns {Promise<{configured: boolean, totalKeysAvailable: number, ...}>}
 */
export async function getVanityPoolStatus() {
  return request('/api/vanity/status');
}
